import React from "react";

import { ReactComponent as ExclamationSvgIcon } from "../../assets/icons/exclamation-circle-solid.svg";

import { css } from "@emotion/react";

import { CSS_COLORS } from "../../styles/styles-css-variables";
import { ContentContainer } from "../_shared-components/arrangement/Container";
import { NonEmptyArray } from "fp-ts/es6/NonEmptyArray";
import { SOError } from "@swo/shared";
import { contentFulEntryLink } from "../cms/contentful/contentful-entry-link";
import { SwarovskiButton } from "../_shared-components/ui-elements/SwarovskiButton";
import { PublicConfigurationT } from "@swo/shared";

const exclamationIconStyles = css`
  width: 50px;
  height: 50px;
`;

export const defaultErrorsHeadline = "Oops! Something went wrong.";
export const defaultErrorsParagraph = "This page couldn't render all components correctly. In the most cases this error caused by missing or inconsistent data in contentful. Please take a look at the following errors."

export function ErrorLogsComponent({
  errors,
  pageId,
  config,
  headline,
  paragraph,
}: {
  errors: NonEmptyArray<SOError>;
  pageId?: string;
  config: PublicConfigurationT;
  headline: string;
  paragraph: string;
}) {
  return (
    <div
      css={css`
        background-color: ${CSS_COLORS.Grey6};
        color: ${CSS_COLORS.Grey3};

        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;
        min-height: 100vh;
      `}
    >
      <ContentContainer>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              max-width: 600px;
              align-items: center;
            `}
          >
            <ExclamationSvgIcon css={exclamationIconStyles} />
            <h2
              css={css`
                margin-top: 5px;
                margin-bottom: 12px;
                font-size: 32px;
              `}
            >
              {headline}
            </h2>

            <span
              css={css`
                font-size: 21px;
                font-weight: 300;
                margin-bottom: 12px;
              `}
            >
              {paragraph}
            </span>

            {pageId ? (
              <SwarovskiButton
                as={"a"}
                isInternal={false}
                target={"_blank"}
                rel="noopener noreferrer"
                link={contentFulEntryLink(pageId, config)}
                variant={"green"}
              >
                Link to this page in Contentful
              </SwarovskiButton>
            ) : null}

            <div
              css={css`
                margin-top: 50px;
                display: flex;
                flex-direction: column;
                align-items: center;
                font-size: 21px;
              `}
            >
              <span
                className={"u-fw-300"}
                css={css`
                  text-align: left;
                `}
              >
                What happened?{" "}
              </span>

              <ol>
                {errors.map((error, idx) => (
                  <Error key={idx} error={error} config={config} />
                ))}
              </ol>
            </div>
          </div>
        </div>
      </ContentContainer>
    </div>
  );
}

function Error(props: { error: SOError; config: PublicConfigurationT }) {
  function getErrorComponent() {
    switch (props.error.__tag) {
      case "basic-error": {
        return (
          <span
            css={css`
              margin-top: 10px;
            `}
          >
            Error message: {props.error.msg}
          </span>
        );
      }

      case "contentful-error": {
        return (
          <React.Fragment>
            <span
              css={css`
                margin-top: 10px;
              `}
            >
              Error source:{" "}
            </span>
            <a
              css={css`
                color: black;
              `}
              target={"_blank"}
              rel="noopener noreferrer"
              href={contentFulEntryLink(props.error.entryId, props.config)}
            >
              {contentFulEntryLink(props.error.entryId, props.config)}
            </a>
          </React.Fragment>
        );
      }

      default: {
        const _: never = props.error;

        return _;
      }
    }
  }
  return (
    <li
      css={css`
        margin: 10px;
        display: flex;
        flex-direction: column;
      `}
    >
      <span
        css={css`
          font-weight: 500;
          color: ${CSS_COLORS.RedCustom};
          font-size: 24px;
        `}
      >
        {props.error.msg}
      </span>

      {getErrorComponent()}
    </li>
  );
}
